import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Stethoscope, Plus, Trash2, Edit, X, Save, Camera, Loader2, ChevronRight } from 'lucide-react';
import { compressImageToBase64 } from '../utils/imageCompress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (d) => { if (!d) return '-'; const p = d.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };

function DateInput({ value, onChange, className = '' }) {
  const toDisplay = (iso) => { if (!iso) return ''; const p = iso.slice(0, 10).split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
  const toIso = (s) => { const p = s.replace(/[^\d/]/g, '').split('/'); if (p.length === 3 && p[0].length === 2 && p[1].length === 2 && p[2].length === 4) return `${p[2]}-${p[1]}-${p[0]}`; return ''; };
  const [text, setText] = useState(toDisplay(value));
  useEffect(() => { setText(toDisplay(value)); }, [value]);
  const onCh = (e) => { let v = e.target.value.replace(/[^\d/]/g, ''); if (v.length === 2 && !v.includes('/')) v += '/'; if (v.length === 5 && v.split('/').length === 2) v += '/'; if (v.length > 10) v = v.slice(0, 10); setText(v); const iso = toIso(v); if (iso) onChange(iso); else if (!v) onChange(''); };
  return <input value={text} onChange={onCh} placeholder="dd/mm/aaaa" maxLength={10} className={`rounded-lg border border-border bg-background px-2 py-1.5 text-sm ${className}`} />;
}

const emptyForm = { nome: '', especie: '', chip: '', telefone: '', clinica_origem: '', tipo_tratamento: '', data: '', observacoes: '', fotos: [] };

export default function TratamentosExternosPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // Quando navegamos com ?new=1 (do botão "Adicionar > Tratamento Externo"),
  // abrir o formulário imediatamente. A lista vive no Dashboard, por isso ao
  // fechar/guardar voltamos ao Dashboard.
  const cameFromAdd = searchParams.get('new') === '1';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(cameFromAdd);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const carregar = () => {
    setLoading(true);
    axios.get(`${API}/tratamentos-externos`).then(r => setItems(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { carregar(); }, []);

  const closeForm = () => {
    setShowForm(false);
    if (cameFromAdd) {
      // volta ao dashboard (rota raiz) onde está a lista
      navigate('/');
    }
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...emptyForm, ...it, fotos: it.fotos || [] }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim()) { alert('Nome obrigatório'); return; }
    setBusy(true);
    try {
      if (editing) {
        await axios.put(`${API}/tratamentos-externos/${editing.id}`, form);
      } else {
        await axios.post(`${API}/tratamentos-externos`, form);
      }
      setShowForm(false);
      if (cameFromAdd) {
        navigate('/');
      } else {
        carregar();
      }
    } catch (e2) {
      alert(e2.response?.data?.detail || 'Erro a guardar');
    } finally { setBusy(false); }
  };

  const apagar = async (id) => {
    if (!window.confirm('Apagar este registo?')) return;
    await axios.delete(`${API}/tratamentos-externos/${id}`);
    carregar();
  };

  const addFotos = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    try {
      const b64s = await Promise.all(list.map(f => compressImageToBase64(f, { maxDim: 1280, quality: 0.8 })));
      // Backend doesn't upload here — we keep them inline as data URLs to send to backend on submit.
      // For Cloudinary upload flow we'd need a separate endpoint; for simplicity keep as base64.
      const dataUrls = b64s.map(b => `data:image/jpeg;base64,${b}`);
      setForm(prev => ({ ...prev, fotos: [...(prev.fotos || []), ...dataUrls] }));
    } catch (_) { alert('Erro a processar imagens'); }
  };

  return (
    <div className="space-y-4">
      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-2xl border border-dashed border-border">
          <Stethoscope className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">Sem tratamentos externos registados.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(it => (
            <button key={it.id} onClick={() => setDetail(it)} data-testid={`item-ext-${it.id}`}
              className="w-full text-left bg-card border border-border rounded-2xl p-3 sm:p-4 hover:shadow-md transition-all flex items-center gap-3 group">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-0.5">
                  <h3 className="font-medium truncate">{it.nome}</h3>
                  {it.especie && <span className="text-[10px] uppercase font-semibold rounded-full px-2 py-0.5 bg-blue-100 text-blue-700">{it.especie}</span>}
                </div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                  {it.clinica_origem && <span>🏥 {it.clinica_origem}</span>}
                  {it.tipo_tratamento && <span>💊 {it.tipo_tratamento}</span>}
                  {it.data && <span>📅 {fmtDate(it.data)}</span>}
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform flex-shrink-0" />
            </button>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-3 pb-24 sm:pb-3" onClick={closeForm}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-card rounded-2xl border border-border shadow-2xl w-full sm:max-w-lg max-h-[80vh] sm:max-h-[85vh] overflow-y-auto" data-testid="form-ext">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg">{editing ? 'Editar' : 'Novo'} tratamento externo</h3>
              <button type="button" onClick={closeForm}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs font-medium block mb-1">Nome do animal *</label>
                <input data-testid="ext-nome" required value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1">Espécie</label>
                  <select value={form.especie} onChange={e => setForm({ ...form, especie: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-2 py-2 text-sm">
                    <option value="">—</option>
                    <option value="cao">Cão</option>
                    <option value="gato">Gato</option>
                    <option value="outro">Outro</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Chip</label>
                  <input value={form.chip || ''} onChange={e => setForm({ ...form, chip: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Clínica de origem</label>
                <input value={form.clinica_origem || ''} onChange={e => setForm({ ...form, clinica_origem: e.target.value })}
                  placeholder="ex: Clínica Pet Center"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Tipo de tratamento</label>
                <input value={form.tipo_tratamento || ''} onChange={e => setForm({ ...form, tipo_tratamento: e.target.value })}
                  placeholder="ex: Banho medicamentoso, Eutanásia"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium block mb-1">Telefone do dono</label>
                  <input value={form.telefone || ''} onChange={e => setForm({ ...form, telefone: e.target.value })}
                    className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs font-medium block mb-1">Data</label>
                  <DateInput value={form.data} onChange={v => setForm({ ...form, data: v })} className="w-full" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium block mb-1">Observações</label>
                <textarea rows={3} value={form.observacoes || ''} onChange={e => setForm({ ...form, observacoes: e.target.value })}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium block mb-1 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Fotos (opcional)</label>
                <input type="file" accept="image/*" multiple onChange={e => addFotos(e.target.files)}
                  className="text-xs" />
                {form.fotos?.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {form.fotos.map((url, i) => (
                      <div key={i} className="relative">
                        <img src={url} alt={`f-${i}`} className="rounded-lg w-full h-16 object-cover border border-border" />
                        <button type="button" onClick={() => setForm({ ...form, fotos: form.fotos.filter((_, idx) => idx !== i) })}
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="sticky bottom-0 bg-card border-t border-border p-3 flex gap-2 justify-end">
              <button type="button" onClick={closeForm} className="px-4 py-2 rounded-full bg-muted text-foreground text-sm">Cancelar</button>
              <button type="submit" disabled={busy} data-testid="ext-submit"
                className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-medium disabled:opacity-50 flex items-center gap-1">
                <Save className="w-4 h-4" /> {busy ? 'A guardar…' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-3 pb-24 sm:pb-3" onClick={() => setDetail(null)}>
          <div onClick={e => e.stopPropagation()} className="bg-card rounded-2xl border border-border shadow-2xl w-full sm:max-w-lg max-h-[80vh] sm:max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between">
              <h3 className="font-heading font-bold text-lg truncate">{detail.nome}</h3>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 space-y-2 text-sm">
              {detail.especie && <p><strong>Espécie:</strong> <span className="capitalize">{detail.especie}</span></p>}
              {detail.chip && <p><strong>Chip:</strong> {detail.chip}</p>}
              {detail.telefone && <p><strong>Telefone:</strong> {detail.telefone}</p>}
              {detail.clinica_origem && <p><strong>Clínica:</strong> {detail.clinica_origem}</p>}
              {detail.tipo_tratamento && <p><strong>Tratamento:</strong> {detail.tipo_tratamento}</p>}
              {detail.data && <p><strong>Data:</strong> {fmtDate(detail.data)}</p>}
              {detail.observacoes && <p className="bg-muted/40 p-2 rounded-lg whitespace-pre-wrap"><strong>Observações:</strong> {detail.observacoes}</p>}
              {detail.fotos?.length > 0 && (
                <div>
                  <p className="text-xs font-medium mb-1">Fotos</p>
                  <div className="grid grid-cols-3 gap-2">
                    {detail.fotos.map((u, i) => <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt={`f${i}`} className="rounded-lg w-full h-20 object-cover border border-border" /></a>)}
                  </div>
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-card border-t border-border p-3 flex gap-2 justify-end">
              <button onClick={() => { setDetail(null); openEdit(detail); }} data-testid="ext-edit"
                className="flex items-center gap-1.5 bg-muted text-foreground rounded-full px-3 py-1.5 text-xs"><Edit className="w-3.5 h-3.5" /> Editar</button>
              <button onClick={async () => { await apagar(detail.id); setDetail(null); }} data-testid="ext-delete"
                className="flex items-center gap-1.5 bg-red-100 text-red-700 rounded-full px-3 py-1.5 text-xs"><Trash2 className="w-3.5 h-3.5" /> Apagar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
